/**
 * Reference photographs shipped with the site, used as stand-ins until the real
 * item has been photographed in the yard.
 *
 * These are studio images of the *type* of part, not of any particular unit we
 * hold. Everything that renders one has to say so: a buyer must never take a
 * stand-in for a photo of the item they are reserving.
 */

import type { BodyType, PartCategory } from '@/types'

export type PhotoGroup = 'part' | 'vehicle' | 'scene'

export interface StockPhoto {
  /** File stem under public/stock, and what gets stored on the document. */
  id: string
  label: string
  group: PhotoGroup
  /** Part categories this photo is a sensible default for. */
  category?: PartCategory
  /** Body types this photo suits, for donor vehicles. */
  body?: BodyType
}

export const STOCK_PHOTOS: readonly StockPhoto[] = [
  // batteries and modules
  { id: '40_kwh_pack', label: '40 kWh pack', group: 'part', category: 'battery' },
  { id: '62_kwh_pack', label: '62 kWh pack', group: 'part', category: 'battery' },
  { id: 'older_30_24_kwh_pack', label: '24 / 30 kWh pack', group: 'part', category: 'battery' },
  { id: 'battery_modules_set_of_four', label: 'Modules, set of four', group: 'part', category: 'module' },
  { id: 'single_module_closeup', label: 'Single module', group: 'part', category: 'module' },
  { id: 'battery_controller', label: 'Battery controller', group: 'part', category: 'controller' },
  // EV drivetrain
  { id: 'traction_motor', label: 'Traction motor', group: 'part', category: 'motor' },
  { id: 'inverter_pdm', label: 'Inverter / PDM', group: 'part', category: 'inverter' },
  { id: 'onboard_charger', label: 'On-board charger', group: 'part', category: 'charger' },
  { id: 'chademo_quick_charge_port', label: 'CHAdeMO port', group: 'part', category: 'port' },
  { id: 'hv_cable_set', label: 'HV cable set', group: 'part', category: 'cable' },
  { id: 'ac_compressor', label: 'A/C compressor', group: 'part', category: 'ac' },
  // mechanical and body
  { id: 'engine', label: 'Engine', group: 'part', category: 'engine' },
  { id: 'gearbox', label: 'Gearbox', group: 'part', category: 'gearbox' },
  { id: 'coilover_suspension_set', label: 'Suspension set', group: 'part', category: 'suspension' },
  { id: 'brake_disc_and_caliper', label: 'Brake disc and caliper', group: 'part', category: 'brake' },
  { id: 'alloy_wheel', label: 'Alloy wheel', group: 'part', category: 'wheel' },
  { id: 'headlight_unit', label: 'Headlight unit', group: 'part', category: 'light' },
  { id: 'front_seat', label: 'Front seat', group: 'part', category: 'interior' },
  { id: 'body_panel', label: 'Body panel', group: 'part', category: 'body' },
  // donor vehicles
  { id: 'silver_hatchback', label: 'Silver hatchback', group: 'vehicle', body: 'hatch' },
  { id: 'white_hatchback', label: 'White hatchback', group: 'vehicle', body: 'hatch' },
  { id: 'dark_blue_compact', label: 'Dark blue compact', group: 'vehicle', body: 'sedan' },
  { id: 'silver_van', label: 'Silver van', group: 'vehicle', body: 'van' },
  // shipment and workshop scenes
  { id: 'container_ship', label: 'Container ship', group: 'scene' },
  { id: 'container_yard', label: 'Container yard', group: 'scene' },
  { id: 'workshop_bay', label: 'Workshop bay', group: 'scene' },
]

const BY_ID = new Map(STOCK_PHOTOS.map((p) => [p.id, p]))

/** Served straight from public/, so no Storage read and no egress cost. */
export const PHOTO_BASE = '/stock/'

export function photoUrl(id: string | null | undefined): string | null {
  return id && BY_ID.has(id) ? `${PHOTO_BASE}${id}.webp` : null
}

export function photoById(id: string | null | undefined): StockPhoto | null {
  return (id && BY_ID.get(id)) || null
}

/** True for a picture that came out of this set rather than off a camera in the yard. */
export function isStockPhoto(src: string | null | undefined): boolean {
  return typeof src === 'string' && src.startsWith(PHOTO_BASE)
}

export function photosIn(group: PhotoGroup): readonly StockPhoto[] {
  return STOCK_PHOTOS.filter((p) => p.group === group)
}

/** The obvious stand-in for a category, so adding stock is one fewer decision. */
export function suggestForCategory(category: string): string | null {
  return STOCK_PHOTOS.find((p) => p.group === 'part' && p.category === category)?.id ?? null
}

export function suggestForBody(body: BodyType): string | null {
  return photosIn('vehicle').find((p) => p.body === body)?.id ?? null
}
